import { Button } from "@/components/ui/button";
import { Instagram, Facebook, Twitter, Mail, MapPin, Phone } from "lucide-react";

interface BusinessData {
  business_name: string | null;
  description: string | null;
  avatar_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  twitter_url: string | null;
  address: string | null;
  phone_number: string | null;
  business_email: string | null;
}

interface HeroSectionProps {
  business: BusinessData;
}

const HeroSection = ({ business }: HeroSectionProps) => {
  const getInitial = (name: string | null) => {
    if (!name) return "B";
    return name.charAt(0).toUpperCase();
  };

  return (
    <section className="text-left animate-fade-up">
      {business.avatar_url ? (
        <div className="w-20 h-20 rounded-full mb-4 overflow-hidden">
          <img src={business.avatar_url} alt="Profile" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-20 h-20 rounded-full bg-primary mb-4 flex items-center justify-center">
          <span className="text-3xl font-bold text-primary-foreground">{getInitial(business.business_name)}</span>
        </div>
      )}
      
      <h1 className="text-2xl font-heading font-bold mb-2">{business.business_name || "Business Name"}</h1>
      {business.description && (
        <p className="text-muted-foreground mb-4">
          {business.description}
        </p>
      )}

      {/* Socials */}
      {(business.instagram_url || business.facebook_url || business.twitter_url) && (
        <div className="flex justify-start gap-3 mb-4">
          {business.instagram_url && (
            <a
              href={business.instagram_url.startsWith('http') ? business.instagram_url : `https://instagram.com/${business.instagram_url.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
          )}
          {business.facebook_url && (
            <a
              href={business.facebook_url.startsWith('http') ? business.facebook_url : `https://facebook.com/${business.facebook_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
          )}
          {business.twitter_url && (
            <a
              href={business.twitter_url.startsWith('http') ? business.twitter_url : `https://twitter.com/${business.twitter_url.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <Twitter className="w-4 h-4" />
            </a>
          )}
        </div>
      )}

      {/* Contact info */}
      <div className="mt-4 space-y-1.5 text-sm text-muted-foreground text-left">
        {business.address && (
          <p className="flex items-center justify-start gap-2">
            <MapPin className="w-4 h-4 text-primary shrink-0" />
            {business.address}
          </p>
        )}
        {business.phone_number && (
          <p className="flex items-center justify-start gap-2">
            <Phone className="w-4 h-4 text-primary shrink-0" />
            {business.phone_number}
          </p>
        )}
        {business.business_email && (
          <p className="flex items-center justify-start gap-2 break-all">
            <Mail className="w-4 h-4 text-primary shrink-0" />
            {business.business_email}
          </p>
        )}
      </div>

      {/* Desktop watermark - below CTAs */}
      <div className="hidden md:block mt-8 pt-6 border-t border-border/50">
        <p className="text-xs text-muted-foreground">
          Powered by{" "}
          <a
            href="https://www.usedibs.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary hover:underline"
          >
            Dibs
          </a>
        </p>
      </div>
    </section>
  );
};

export default HeroSection;